import { SensorConversions } from "../conversionFunctions/sensorConversions.js";
import { sensorParameterNumericValidators } from "../SensorConfigurationParameterHelpers.js";
import { SensorParameterConfigBase, SensorParameterValueType } from "../SensorConfigurationTypes.js";
import { SensorElementConfig } from "./types.js";

const soilTensionParams = {
    slopeKpaPerVolt: {
        key: "slopeKpaPerVolt",
        type: SensorParameterValueType.NUMBER,
        name: "Slope (kPa/V)",
        description: "Soil tension gain: how many kPa the output increases per volt of input signal.",
        defaultValue: 1,
        validator: sensorParameterNumericValidators.isNumber,
        mustBeNonNegative: false,
        mustBeInteger: false,
        parameterUnitCategory: "pressureTransducerSlope",
    },
    offsetKpa: {
        key: "offsetKpa",
        type: SensorParameterValueType.NUMBER,
        name: "Offset (kPa)",
        description:
            "Soil tension offset added to slope × voltage. Typically a negative value that zeros the output at the sensor's minimum voltage.",
        defaultValue: 0,
        validator: sensorParameterNumericValidators.isNumber,
        mustBeNonNegative: false,
        mustBeInteger: false,
        parameterUnitCategory: "liquidPressure",
    },
} as const satisfies Record<string, SensorParameterConfigBase<number>>;

export const soilTensionElementConfig: SensorElementConfig<
    typeof soilTensionParams,
    typeof SensorConversions.linearWithMinAndMax
> = {
    legalConvertedRange: [0, null],
    conversionKey: "linearWithMinAndMax",
    parameterConfigs: soilTensionParams,
    buildConversionParams: ({ slopeKpaPerVolt, offsetKpa }) => ({
        slope: slopeKpaPerVolt,
        offset: offsetKpa,
        outputMin: 0,
        outputMax: Number.MAX_VALUE,
    }),
    decodedUnits: "volt",
    convertedUnits: "kilopascal",
    unitCategory: "soilTension",
    testCases: [
        {
            // Watermark 200SS: slope=85.357 kPa/V, offset=0.
            //   at 1 V: 85.357 * 1 + 0 = 85.357 kPa
            parameters: { slopeKpaPerVolt: 85.357, offsetKpa: 0 },
            decodedValue: 1,
            convertedValue: 85.357,
        },
        {
            // Irrometer Tensiometer: slope=23.5 kPa/V, offset=-11.75.
            //   at 1 V: 23.5 * 1 - 11.75 = 11.75 kPa
            parameters: { slopeKpaPerVolt: 23.5, offsetKpa: -11.75 },
            decodedValue: 1,
            convertedValue: 11.75,
        },
        {
            // Irrometer Tensiometer below its zeroing voltage: clamped to 0 by outputMin.
            //   23.5 * 0.2 - 11.75 = 4.7 - 11.75 = -7.05 kPa -> clamped to 0
            parameters: { slopeKpaPerVolt: 23.5, offsetKpa: -11.75 },
            decodedValue: 0.2,
            convertedValue: 0,
            skipBackwardConversionCheck: true,
        },
    ],
} as const;
